import { useId, useState, type FormEvent } from 'react';
import { AuthError, changePassword } from './auth.js';

/**
 * Changing the password of the signed-in account (FR-1.8).
 *
 * Distinct from the reset on the sign-in page, which is for someone who cannot
 * get in (FR-1.4). This one is for someone already in, so it asks for the
 * current password rather than emailing a code -- proof of possession of the
 * session is not proof of knowing the password, and an unlocked device should
 * not be enough to lock its owner out.
 *
 * Behind a disclosure, like deleting an item. Account is mostly a page you read
 * -- who you are signed in as, the currency, the way out -- and three password
 * fields permanently open would make it a form you pass through instead. The
 * press is what says you meant to.
 *
 * It does not sign other devices out; `auth.ts` says why that would lose data.
 */
export function ChangePassword() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const headingId = useId();
  const currentId = useId();
  const nextId = useId();
  const errorId = useId();

  const close = () => {
    setOpen(false);
    setCurrent('');
    setNext('');
    setError(null);
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        await changePassword({ currentPassword: current, newPassword: next });
        close();
        setDone(true);
      } catch (caught) {
        // AuthError messages are written for a person; anything else must not
        // put an exception string on the page.
        setError(
          caught instanceof AuthError
            ? caught.message
            : 'Could not change that password. Please try again.',
        );
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <section aria-labelledby={headingId}>
      <h3 id={headingId}>Password</h3>

      {/*
        Announced, because the form it replaces has gone: without this the only
        evidence of success is three fields disappearing, which reads the same
        as a form that gave up.
      */}
      {done && !open && (
        <p className="muted" role="status">
          Your password has been changed. Other devices stay signed in.
        </p>
      )}

      <div className="actions">
        <button
          type="button"
          onClick={() => {
            if (open) {
              close();
              return;
            }
            setDone(false);
            setOpen(true);
          }}
        >
          {open ? 'Cancel' : 'Change password'}
        </button>
      </div>

      {open && (
        <form onSubmit={onSubmit} noValidate>
          <div className="field">
            <label htmlFor={currentId}>Current password</label>
            <input
              id={currentId}
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
              disabled={busy}
              {...(error === null ? {} : { 'aria-describedby': errorId })}
            />
          </div>

          <div className="field">
            <label htmlFor={nextId}>New password</label>
            <input
              id={nextId}
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              required
              disabled={busy}
              aria-describedby={error === null ? `${nextId}-hint` : errorId}
            />
            {/* The policy up front rather than only on rejection, as the reset
                and the forced first change both do. */}
            <p id={`${nextId}-hint`} className="muted hint">
              At least 12 characters, with upper and lower case, a number and a symbol.
            </p>
          </div>

          {/* Announced on change, and not conveyed by colour alone (NFR-UX-3).
              The text sits directly in the region so it cannot outgrow the line
              reserved for it and shift the button as it arrives. */}
          <p id={errorId} className="warn" role="alert" aria-live="polite">
            {error ?? ''}
          </p>

          <div className="actions">
            <button type="submit" className="primary" disabled={busy}>
              {busy ? 'Working…' : 'Change password'}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
